import IDisplayContainer from "../interfaces/IDisplayContainer";
import { Container, ContainerOptions, uid } from "pixi.js";
import DagobahModel from "../models/DagobahModel.ts";

export default class DisplayContainer extends Container implements IDisplayContainer {
    //--------------------------------------------------------------------------
    //
    //  Properties
    //
    //--------------------------------------------------------------------------

    //----------------------------------
    //  Private:
    //----------------------------------

    private _id: number = uid();
    
    //----------------------------------
    //  Protected:
    //----------------------------------

    protected DEBUG_ALPHA: number = 1;
    protected DEBUG_COLOR_BB: number = 0x00ff00;
    protected DEBUG_COLOR_PP: number = 0xe600ff;
    protected DEBUG_LINE_WIDTH: number = 1;

    protected _enabled: boolean;
    protected appModel: DagobahModel = DagobahModel.getInstance();
    protected eventDispatcher: EventTarget = new EventTarget();
    protected customEvents: Map<string, { listener: EventListenerOrEventListenerObject; options?: boolean | EventListenerOptions }> = new Map();

    public get enabled(): boolean {
        return this.isInteractive();
    }
    
    public get id(): number {
        return this._id;
    }

    //--------------------------------------------------------------------------
    //
    //  Constructor
    //
    //--------------------------------------------------------------------------

    constructor(options?: ContainerOptions) {
        super(options);
    }

    //--------------------------------------------------------------------------
    //
    //  Methods
    //
    //--------------------------------------------------------------------------

    //----------------------------------
    //  Public:
    //----------------------------------

    public addCustomEventListener(eventName: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions): void {
        this.customEvents.set(eventName, { listener, options });
        this.eventDispatcher.addEventListener(eventName, listener, options);
    }

    public create(creationCompleteCallback?: () => void, show = true, enabled = true): void {
        if (show) {
            this.show();
        }
        if (!enabled) {
            this.disable();
        }
    }

    public destroy(): void {
        let curChild: Container;
        const len = this.children.length;
        for (let i: number = 0; i < len; i++) {
            curChild = this.getChildAt(0);
            if (curChild.destroy != undefined) {
                curChild.destroy();
            }
            if (curChild.removeAllListeners != undefined) {
                curChild.removeAllListeners();
            }
            if (curChild.removeChildren != undefined) {
                curChild.removeChildren();
            }
            curChild = null;
        }
        this.removeAllCustomEventListeners();
        this.removeAllListeners();
        this.removeChildren();
        this.appModel = null;
        this.eventDispatcher = null;
        this.customEvents = null;
        //console.log(`${this.label} destroyed`);
    }

    public disable(): void {
        this.eventMode = "passive";
        this.interactiveChildren = false;
    }

    public dispatchCustomEvent(event: Event): boolean {
        return this.eventDispatcher.dispatchEvent(event);
    }

    public enable(): void {
        this.eventMode = "static";
        this.interactiveChildren = true;
    }

    public hide(): void {
        this.visible = false;
    }

    public removeCustomEventListener(eventName: string, listener: EventListenerOrEventListenerObject, options?: boolean | EventListenerOptions): void {
        this.customEvents.delete(eventName);
        this.eventDispatcher.removeEventListener(eventName, listener, options);
    }

    public removeAllCustomEventListeners(): void {
        if (this.customEvents) {
            this.customEvents.forEach(({ listener, options }, eventName) => {
                this.eventDispatcher.removeEventListener(eventName, listener, options);
            });
            this.customEvents.clear();
        }
    }

    public show(): void {
        this.visible = true;
    }
}
